/* Los Excel que llegaron al bot → planillas de Mis planillas (FiboSheets #10).
 *
 * ── POR QUÉ ACÁ Y NO EN EL BOT ─────────────────────────────────────────────
 *
 * La gente ya recibe sus Excel por mail: ahora se los reenvía al bot (por
 * WhatsApp o a la etiqueta «FiboSheets» de la casilla de Cepi) y aparecen
 * solos. El bot sólo RECIBE: sube el archivo y deja la fila pendiente. La
 * conversión se hace acá, con `importarLibro` —la misma del botón «Importar
 * Excel»—, porque decidir si una fórmula queda viva necesita el motor de
 * fórmulas, que es éste. Una segunda conversión en Python daría, con el
 * tiempo, otra planilla para el mismo archivo.
 *
 * El precio es que se convierte cuando el dueño abre Mis planillas, con su
 * sesión: `crearPlanilla` la deja a su nombre como cualquier otra.
 *
 * ── CADA PENDIENTE ─────────────────────────────────────────────────────────
 *
 *   1. Se TOMA (UPDATE condicionado a que siga pendiente): dos pestañas
 *      abiertas no crean dos planillas.
 *   2. Se baja el archivo, se convierte y se crea la planilla.
 *   3. Se marca `hecha` (o `error`, con el porqué) y se borra el archivo.
 *
 * Lo que es del ARCHIVO (no se lee, pasa el tope de una planilla, ya
 * no está) termina en error: reintentar no lo arregla. Lo que es NUESTRO (la
 * red, la base) la devuelve a pendiente, y se reintenta la próxima vez.
 *
 * `procesarImportaciones` recibe sus dependencias para poder probarlo sin
 * Supabase; la pantalla usa las de verdad.
 */

import { avisoFormulas } from "./importarExcel";
import { importarLibro } from "./planillaExcel";
import { TOPE_PLANILLA_MB } from "./topes";

/* Las mismas medidas que «Importar Excel» en MiEspacio (COLS_NUEVA y el
   lugar de más para seguir cargando). */
const COLS_NUEVA = 26;
const FILAS_MIN = 50;
const FILAS_DE_MAS = 20;

const ORIGEN = { whatsapp: "WhatsApp", mail: "mail" };

/* «Costos.xlsx» → «Costos», como el botón. */
export function nombrePlanilla(nombreArchivo) {
  return String(nombreArchivo || "").replace(/\.[^.]+$/, "").trim() || "Importada";
}

/* Lo que se le dice de las fórmulas: el mismo texto que el botón.
   `extra` trae `arrastradas`, `recalculadas` y `revisar` (ver
   `importarLibro`). */
export function resumenFormulas(vivas, congeladas, extra = {}) {
  return avisoFormulas({ ...extra, vivas, congeladas }) || "";
}

/* El archivo → lo que se guarda en `espacio_planillas.datos` (la forma de
   `empaquetarLibro` de MiEspacio: `{ hojas: [{ nombre, filas, cols, celdas,
   formato, anchos, altos, fija }], activa }`). */
export function libroDesdeArchivo(XLSX, buffer) {
  // `cellStyles` es lo que hace que lleguen los anchos de columna.
  const wb = XLSX.read(buffer, { type: "array", cellFormula: true, cellNF: true, cellStyles: true });
  const { hojas, vivas, congeladas, arrastradas, recalculadas, revisar } = importarLibro(XLSX, wb);
  const conLugar = hojas.map((h) => {
    const cols = Math.max(COLS_NUEVA, (h.celdas[0] ? h.celdas[0].length : 0) + 1);
    const filas = Math.max(FILAS_MIN, h.celdas.length + FILAS_DE_MAS);
    const celdas = Array.from({ length: filas }, (_, r) => {
      const g = h.celdas[r] ? h.celdas[r].slice() : [];
      while (g.length < cols) g.push("");
      return g;
    });
    return {
      nombre: h.nombre, filas, cols, celdas,
      formato: h.formato || {}, anchos: h.anchos || {}, altos: h.altos || {}, fija: h.fija || {},
    };
  });
  const vacia = () => {
    const celdas = Array.from({ length: 100 }, () => new Array(COLS_NUEVA).fill(""));
    return { nombre: "Hoja 1", filas: 100, cols: COLS_NUEVA, celdas, formato: {}, anchos: {}, altos: {}, fija: {} };
  };
  return { datos: { hojas: conLugar.length ? conLugar : [vacia()], activa: 0 }, vivas, congeladas, arrastradas, recalculadas, revisar };
}

const minuscula = (s) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);

export function textoHecha(imp, resumen) {
  const base = `Llegó «${imp.nombre}» por ${ORIGEN[imp.origen] || imp.origen}: la importé.`;
  return resumen ? `${base} ${resumen}` : base;
}

export function textoError(imp, error) {
  return `Llegó «${imp.nombre}» por ${ORIGEN[imp.origen] || imp.origen}, pero no la pude importar: ${minuscula(error || "algo falló.")}`;
}

const esMuyGrande = (e) => e && (e.code === "23514" || /datos_chicos/.test(e.message || ""));
const noEsta = (e) => e && (/not.?found/i.test(e.message || "") || e.statusCode === "404" || e.status === 404);

export const ERROR_LECTURA = "No pude leer el archivo. ¿Es un Excel (.xlsx) o un CSV?";
export const ERROR_GRANDE = `Pasa el tope de ${TOPE_PLANILLA_MB} MB de una planilla. Si tiene hojas que no necesitás, borralas y mandalo de nuevo.`;
export const ERROR_NO_ESTA = "El archivo ya no está. Mandalo de nuevo.";

/* Atiende todo lo que el bot dejó para este usuario. Devuelve los avisos para
   mostrar: `[{ id, tipo: "ok" | "error", texto, planillaId? }]`. No lanza. */
export async function procesarImportaciones({ svc, crearPlanilla, cargarXLSX, ahora = () => new Date() }) {
  let lista;
  try {
    lista = await svc.importacionesParaAtender();
  } catch {
    return []; // sin poder leer, la próxima vez
  }
  const avisos = [];
  let XLSX = null;
  for (const imp of lista) {
    // Las de error que dejó el bot (un mail sin Excel, uno que pasa el
    // tope): se muestran una vez.
    if (imp.estado === "error") {
      try { await svc.marcarAvisada(imp.id, ahora()); } catch { /* se vuelve a mostrar */ }
      avisos.push({ id: imp.id, tipo: "error", texto: textoError(imp, imp.error) });
      continue;
    }
    let tomada = false;
    try {
      tomada = await svc.tomarImportacion(imp.id, ahora());
    } catch { tomada = false; }
    if (!tomada) continue; // la tiene otra pestaña

    const fallar = async (texto) => {
      try { await svc.marcarError(imp.id, texto, ahora()); } catch { /* queda procesando: vence sola */ }
      await svc.borrarArchivo(imp.ruta);
      avisos.push({ id: imp.id, tipo: "error", texto: textoError(imp, texto) });
    };
    const soltar = async () => {
      try { await svc.soltarImportacion(imp.id); } catch { /* vence sola */ }
    };

    if (!imp.ruta) { await fallar(ERROR_NO_ESTA); continue; }

    let buffer;
    try {
      buffer = await svc.bajarArchivo(imp.ruta);
    } catch (e) {
      if (noEsta(e)) await fallar(ERROR_NO_ESTA);
      else await soltar();
      continue;
    }

    let libro;
    try {
      if (!XLSX) XLSX = await cargarXLSX();
      libro = libroDesdeArchivo(XLSX, buffer);
    } catch {
      if (!XLSX) { await soltar(); continue; } // no cargó SheetJS: es nuestro
      await fallar(ERROR_LECTURA);
      continue;
    }

    let planillaId;
    try {
      planillaId = await crearPlanilla(nombrePlanilla(imp.nombre), libro.datos);
    } catch (e) {
      if (esMuyGrande(e)) await fallar(ERROR_GRANDE);
      else await soltar();
      continue;
    }
    try {
      await svc.marcarHecha(imp.id, planillaId, ahora());
    } catch { /* la planilla ya está: que no se pierda el aviso */ }
    await svc.borrarArchivo(imp.ruta);
    avisos.push({
      id: imp.id, tipo: "ok", planillaId,
      texto: textoHecha(imp, resumenFormulas(libro.vivas, libro.congeladas, libro)),
    });
  }
  return avisos;
}
